import React, {ChangeEvent} from "react";
import styles from "./index.module.css";
import {Button, DropdownItemProps, DropdownProps, Form, Grid, Loader} from "semantic-ui-react";
import agent, {PaginateOptions} from "../../../agent";
import {Observer} from "../../dashboard";
import {Link, Redirect, RouteComponentProps} from "react-router-dom";
import {extractReturnUrl, handleGoBack} from "../../../helpers/TablePageUtils";
import { textLabels } from "../../../consts/textLabels";
import {validateName} from "../../../consts/onKeyDownValidation";
import PasswordInput from "../../common/PasswordInput";

interface MatchParams {
    id: string;
}

interface Props extends RouteComponentProps<MatchParams> {}

interface State {
    editable: boolean,
    _id: string,
    username: string,
    name: string,
    middlename: string,
    surname: string,
    tel: string,
    email: string,
    loading: boolean,
    saved: boolean,
    editMode: boolean;
    login?: string;
    password?: string;
    password2?: string;
    passwordChangeMode: boolean;
    passwordError?: string;
    password2Error?: string;
    nameValid: boolean,
    surnameValid: boolean,
    middlenameValid: boolean,
    roleEnableScan: boolean,
    roleEnableFileUpload?: boolean;
    returnUrl: string;
}

class ObserverEditor extends React.PureComponent<Props, State> {
    private readonly FALLBACK_URL = "/dashboard/observers";
    private readonly showLogin: boolean = window.config?.useObserverAuthenticationByPassword === true;

    state: State = {
        editable: false,
        _id: '',
        username: '',
        name: '',
        middlename: '',
        surname: '',
        tel: '',
        email: '',
        loading: false,
        editMode: false,
        saved: false,
        passwordChangeMode: false,
        nameValid: true,
        surnameValid: false,
        middlenameValid: true,
        roleEnableScan: false,
        roleEnableFileUpload: false,
        returnUrl: this.FALLBACK_URL,
    };

    async componentDidMount() {
        const extractedUrl = extractReturnUrl(this.props.location, this.FALLBACK_URL);
        this.setState({ returnUrl: extractedUrl });
        if (this.props.match && this.props.match.path.includes('edit')) {
            this.setState({editMode: true, loading: true, passwordChangeMode: false});
            await this.onMount();
        } else {
            this.setState({editable: true, loading: false, passwordChangeMode: true})
        }
    };

    onMount = async (): Promise<void> => {
        const observerId: string = this.props.match ? this.props.match.params.id : '';
        const doc: Observer = await agent.ObserverProvider.open(observerId);

        if (doc) {
            this.setState({
                _id: doc._id,
                username: doc.username,
                name: doc.name,
                middlename: doc.middlename,
                surname: doc.surname,
                tel: doc.tel,
                email: doc.email,
                loading: false,
                login: doc.login,
                passwordChangeMode: !doc.hasPassword,
                roleEnableScan: !!doc.roleEnableScan,
                roleEnableFileUpload: !!doc.roleEnableFileUpload
            });
        }
    };

    onCreate = async (): Promise<void> => {
        if (!this.checkPasswords()) return;
        this.setState({loading: true});

        await agent.ObserverProvider.create({
            _id: '',
            username: this.state.username,
            name: this.state.name,
            middlename: this.state.middlename,
            surname: this.state.surname,
            tel: this.state.tel,
            email: this.state.email,
            login: this.state.login,
            password: this.state.password,
            roleEnableScan: this.state.roleEnableScan,
            roleEnableFileUpload: this.state.roleEnableFileUpload
        });

        this.setState({loading: false, saved: true});
    };

    onEdit = async () => {
        if (!this.checkPasswords()) return;
        this.setState({loading: true});

        await agent.ObserverProvider.edit({
            _id: this.state._id,
            username: this.state.username,
            name: this.state.name,
            middlename: this.state.middlename,
            surname: this.state.surname,
            tel: this.state.tel,
            email: this.state.email,
            login: this.state.login,
            password: this.state.password,
            roleEnableScan: this.state.roleEnableScan,
            roleEnableFileUpload: this.state.roleEnableFileUpload
        }).then((doc: Observer) => {
            this.setState({
                _id: doc._id,
                username: doc.username,
                name: doc.name,
                middlename: doc.middlename,
                surname: doc.surname,
                tel: doc.tel,
                email: doc.email,
                editable: false,
                login: doc.login,
                roleEnableScan: !!doc.roleEnableScan,
                roleEnableFileUpload: !!doc.roleEnableFileUpload
            });
        });

        this.setState({loading: false, saved: true});
    };

    isSaveDisabled = (): boolean => {
        return !this.state.surname
            || !this.state.surnameValid
            || !this.state.nameValid
            || !this.state.middlenameValid
            || (this.showLogin && !this.state.login);
    };

    checkPasswords = (): boolean => {
        if (this.state.editable && this.showLogin && this.state.passwordChangeMode) {
            if (!!this.state.login) {
                if (!this.state.password) {
                    this.setState({passwordError: "Задайте пароль пользователя"});
                    return false;
                } else if ((this.state.password?.length ?? 0) < 4) {
                    this.setState({passwordError: "Минимальная длина пароля - 4 символа!"});
                    return false;
                } else if (this.state.password! !== this.state.password2) {
                    this.setState({password2Error: "Введенные пароли не совпадают!"});
                    return false;
                }
            }
        }
        return true;
    };

    handleGoBack = (e: React.MouseEvent) => {
        e.preventDefault();
        handleGoBack(this.props.history, this.state.returnUrl, this.FALLBACK_URL);
    };

    render() {
        const headerLabel: string = this.state.editMode ? `Данные наблюдателя` : `Создание наблюдателя`;
        const showPassword: boolean = this.showLogin && this.state.editable && this.state.passwordChangeMode;

        return (
            this.state.saved ?
                <Redirect to={this.state.returnUrl || this.FALLBACK_URL}/>
                :
                <div className={styles.observerInfoContent}>
                    <div className={styles.observerInfoHeader}>
                        {headerLabel}
                    </div>
                    {
                        this.state.loading ?
                            <Loader active/>
                            :
                            <Form>
                                <Grid>
                                    <Grid.Column width={4}>
                                        <Form.Input label={'Фамилия'}
                                                    required={true}
                                                    value={this.state.surname}
                                                    readOnly={!this.state.editable}
                                                    onChange={(e) => {
                                                        const { filteredValue, isValid } = validateName(e.target.value);
                                                        this.setState({ surname: filteredValue, surnameValid: isValid });
                                                    }}
                                                    error={!this.state.surnameValid && this.state.editable}
                                                    content={!this.state.surnameValid && this.state.surname !== '' && "Требуется длина от 2 букв"}
                                        />
                                        <Form.Input label={'Имя'}
                                                    value={this.state.name}
                                                    readOnly={!this.state.editable}
                                                    onChange={(e) => {
                                                        const { filteredValue, isValid } = validateName(e.target.value, false);
                                                        this.setState({ name: filteredValue, nameValid: isValid });
                                                    }}
                                                    error={!this.state.nameValid && this.state.editable}
                                                    content={!this.state.nameValid && this.state.name !== '' && "Требуется длина от 2 букв"}
                                        />
                                        <Form.Input label={'Отчество'}
                                                    value={this.state.middlename}
                                                    readOnly={!this.state.editable}
                                                    onChange={(e) => {
                                                        const { filteredValue, isValid } = validateName(e.target.value, false);
                                                        this.setState({ middlename: filteredValue, middlenameValid: isValid });
                                                    }}
                                                    error={!this.state.middlenameValid && this.state.editable}
                                                    content={!this.state.middlenameValid && this.state.middlename !== '' && "Требуется длина от 2 букв"}
                                        />
                                        <Form.Input label={'Электронная почта'}
                                                    value={this.state.email}
                                                    readOnly={!this.state.editable}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({email: e.target.value})}
                                        />
                                        <Form.Input label={'Номер телефона'}
                                                    value={this.state.tel}
                                                    readOnly={!this.state.editable}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({tel: e.target.value})}
                                        />
                                        <Form.Input label={'Telegram username'}
                                                    value={this.state.username}
                                                    readOnly={!this.state.editable}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({username: e.target.value})}
                                        />

                                    </Grid.Column>

                                    <Grid.Column width={6}>
                                        <div className={styles.observerEditorButtons}>

                                            {
                                                this.state.editable ?
                                                    <Button
                                                        className={styles.button}
                                                        color={'green'}
                                                        type={'submit'}
                                                        content={textLabels.saveButton}
                                                        disabled={this.isSaveDisabled()}
                                                        onClick={this.state.editMode ? this.onEdit : this.onCreate}
                                                    />
                                                    :
                                                    <Button
                                                        className={styles.button}
                                                        color={'teal'}
                                                        type={'submit'}
                                                        content={textLabels.editButton}
                                                        onClick={() => this.setState({editable: true, surnameValid: true})}
                                                    />
                                            }

                                            <Button
                                                className={styles.button}
                                                color="grey"
                                                type="button"
                                                content={textLabels.returnButton}
                                                onClick={this.handleGoBack}
                                            />

                                        </div>
                                        <div className={styles.observerEditorPassword}>
                                            {this.showLogin && <Form.Input label={'Логин'}
                                                                      value={this.state.login}
                                                                      readOnly={!this.state.editable}
                                                                      required={true}
                                                                      error={!this.state.login && this.state.editable}
                                                                      onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({login: e.target.value})}

                                            />}

                                            {showPassword && <div>
                                                <PasswordInput label={'Введите пароль'}
                                                               value={this.state.password}
                                                               readOnly={!this.state.editable}
                                                               error={this.state.passwordError}
                                                               onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({password: e.target.value})}
                                                />
                                                <PasswordInput label={'Повторите пароль'}
                                                               value={this.state.password2}
                                                               readOnly={!this.state.editable}
                                                               error={this.state.password2Error}
                                                               onChange={(e: ChangeEvent<HTMLInputElement>) => this.setState({password2: e.target.value})}
                                                /></div>}

                                            {this.state.editable && this.showLogin && !this.state.passwordChangeMode &&
                                            <Form.Button content={"Сменить пароль"} color={"orange"} onClick={() => {
                                                this.setState({passwordChangeMode: true});
                                            }}/>}
                                        </div>

                                    </Grid.Column>
                                </Grid>
                            </Form>
                    }
                </div>
        )
    }
}

export default ObserverEditor;